import React from 'react';

import { ConsoleShell } from '../../../design-system/components/shells';
import {
  Button,
  Drawer,
  PageHeader,
  Pagination,
  Pill,
  Table,
  Toolbar,
} from '../../../design-system/components/desktop';
import {
  ActionRow,
  ConfirmDialog,
  ConsoleCell,
  ConsoleDenied,
  ConsoleEmpty,
  ConsoleError,
  ConsoleStack,
  DetailList,
  DetailRow,
  DetailSection,
} from '../../../design-system/patterns/console';

/**
 * Users, the view. Pure.
 *
 * WHAT DIED HERE
 * --------------
 * `data.table.jsx` with its hand-rolled skeleton, its bare `<input>` search box
 * and its two ad-hoc badge families (`.staff-badge.staff-yes` and
 * `.verification-badge.verified`, each with their own colours), plus
 * `components/UI/UserDetailsCard/` and its gradient Activate button.
 *
 * THREE PILLS, ONE COMPONENT
 * --------------------------
 * Active, Staff and Email verified were three different hand-rolled badges. All
 * three are the same `Pill` now. None of them is a status from
 * `utils/orderStatus.js`, so they take a tone directly rather than going through
 * `StatusPill`: these are booleans about an account, not a point in an order's
 * life. Active is success or the default grey (inactive is a fact, not a
 * failure), staff is ink because it is a system statement, and unverified is
 * warning because it is something the user still has to do.
 *
 * DEACTIVATE IS DESTRUCTIVE, so it is a ghost button whose label says so plus a
 * confirmation. No red button, and `.dsk-btn--danger` does not exist.
 *
 * ONE OVERLAY AT A TIME
 * ---------------------
 * Opening the deactivation confirmation CLOSES the drawer, and cancelling brings
 * it back. That is the upstream template's rule verbatim: "the drawer steps
 * aside so the reason form is the only thing asking for a decision". It is not
 * only a taste call. `Drawer` and `Modal` share
 * `--ds-z-modal` and both install a focus trap through `useModalLayer`, so two
 * open at once means two traps fighting over Tab and an Escape that closes both.
 * `open` is derived, not a second piece of state, so the two cannot disagree.
 */

function ActivePill({ active }) {
  return <Pill tone={active ? 'success' : 'default'}>{active ? 'Active' : 'Inactive'}</Pill>;
}

export function AdminUsersView({
  users = [],
  loading = false,
  error,
  onRetry,
  denied = false,

  search = '',
  onSearchChange,

  page = 1,
  pageCount = 1,
  pageSize = 10,
  total = 0,
  onPageChange,

  selected,
  onOpen,
  onClose,

  onActivate,
  onDeactivate,
  actionBusy = false,
  confirmDeactivateOpen = false,
  onConfirmDeactivateOpen,
  onConfirmDeactivateClose,
}) {
  const columns = [
    {
      key: 'full_name',
      header: 'User',
      render: (row) => (
        <ConsoleCell
          title={row.full_name || 'No name supplied'}
          sub={row.email}
          initials={(row.full_name || row.email || 'U').slice(0, 2)}
        />
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (row) => <span style={{ textTransform: 'capitalize' }}>{row.role || 'Unknown'}</span>,
    },
    { key: 'phone_number', header: 'Phone', muted: true, render: (row) => row.phone_number || 'Not supplied' },
    { key: 'is_active', header: 'Account', render: (row) => <ActivePill active={row.is_active} /> },
    {
      key: 'email_verified',
      header: 'Email',
      render: (row) =>
        row.email_verified ? (
          <Pill tone="success">Verified</Pill>
        ) : (
          <Pill tone="warning">Unverified</Pill>
        ),
    },
    {
      key: 'is_staff',
      header: 'Staff',
      render: (row) => (row.is_staff ? <Pill tone="ink">Staff</Pill> : null),
    },
  ];

  const shell = (children) => (
    <ConsoleShell
      variant="admin"
      title="Users"
      breadcrumbs={[{ label: 'People' }, { label: 'Users' }]}
      showSearch={false}
    >
      {children}
    </ConsoleShell>
  );

  if (denied) return shell(<ConsoleDenied />);

  return shell(
    <>
      <ConsoleStack>
        <PageHeader
          title="Users"
          sub={loading ? 'Loading accounts' : `${users.length} shown of ${total} accounts`}
        />

        <Table
          columns={columns}
          data={error ? [] : users}
          rowKey="id"
          loading={loading}
          onRowClick={(row) => onOpen(row)}
          emptyState={
            error ? (
              <ConsoleError error={error} onRetry={onRetry} />
            ) : (
              <ConsoleEmpty
                filtered={Boolean(search)}
                /* Not the `no-orders-yet` default: this table is not a ledger,
                   and a parcel over "No accounts yet" says the wrong noun. */
                illustration="no-accounts-yet"
                title="No accounts yet"
                text="Accounts appear here as people register."
                filteredText="No account matches that search. Clear it to see every registered account again."
                onClear={() => onSearchChange && onSearchChange('')}
              />
            )
          }
          rowActions={(row) => (
            <Button
              variant="ghost"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                onOpen(row);
              }}
            >
              View
            </Button>
          )}
          toolbar={
            <Toolbar
              search={search}
              onSearchChange={onSearchChange}
              searchPlaceholder="Search by name, email or phone"
              searchLabel="Search users"
            />
          }
          pagination={
            pageCount > 1 ? (
              <Pagination
                page={page}
                pageCount={pageCount}
                pageSize={pageSize}
                totalItems={total}
                itemNoun="accounts"
                onPageChange={onPageChange}
              />
            ) : null
          }
        />
      </ConsoleStack>

      <Drawer
        open={Boolean(selected) && !confirmDeactivateOpen}
        onClose={onClose}
        kicker="Account"
        title={selected?.full_name || selected?.email || 'Account'}
        footer={
          selected ? (
            <ActionRow>
              {selected.is_active ? (
                <Button variant="ghost" onClick={onConfirmDeactivateOpen} disabled={actionBusy}>
                  Deactivate account
                </Button>
              ) : (
                <Button variant="primary" onClick={onActivate} disabled={actionBusy}>
                  {actionBusy ? 'Working' : 'Activate account'}
                </Button>
              )}
            </ActionRow>
          ) : null
        }
      >
        {selected ? (
          <DetailSection>
            <DetailList>
              <DetailRow label="Email" value={selected.email} />
              <DetailRow
                label="Role"
                value={
                  selected.role ? (
                    <span style={{ textTransform: 'capitalize' }}>{selected.role}</span>
                  ) : undefined
                }
              />
              <DetailRow label="Phone" value={selected.phone_number} />
              <DetailRow
                label="Account"
                value={<ActivePill active={selected.is_active} />}
                keepEmpty
              />
              <DetailRow
                label="Email verified"
                value={
                  selected.email_verified ? (
                    <Pill tone="success">Verified</Pill>
                  ) : (
                    <Pill tone="warning">Unverified</Pill>
                  )
                }
                keepEmpty
              />
              <DetailRow
                label="Staff member"
                value={selected.is_staff ? <Pill tone="ink">Staff</Pill> : 'No'}
                keepEmpty
              />
            </DetailList>
          </DetailSection>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={confirmDeactivateOpen}
        onClose={onConfirmDeactivateClose}
        onConfirm={onDeactivate}
        title="Deactivate account"
        confirmLabel="Deactivate account"
        busy={actionBusy}
      >
        <p>
          <strong>{selected?.full_name || selected?.email}</strong> will not be able to sign in
          until the account is activated again.
        </p>
      </ConfirmDialog>
    </>,
  );
}

export default AdminUsersView;
